'use client'

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useGetPostShipTruckTestLayoutDetailsQuery, useCreatePostShipTruckTestLayoutMutation } from '@/redux/slices/preship/preship-api-slice';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { backendApiUrl } from "@/lib/config";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Ellipsis, RefreshCcw, Trash, Download, FileIcon, Loader2 } from "lucide-react";
import FullscreenViewDialog from "../fullscreen-view-dialog";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import AlertTooltip from '@/components/alert-tooltip';
import { TruckLayoutService } from "@/services/TruckLayoutService";
import { Skeleton } from "@/components/ui/skeleton";
import oktaService from "@/services/OktaService";
import { refetchAfterSave } from "@/lib/refetchAfterSave";
const validationSchema = Yup.object().shape({
  pickupName: Yup.string().required('Pickup Name is required'),
  deliveryName: Yup.string().required('Delivery Name is required'),
  pickupPhone: Yup.number().required('Pickup Phone is required'),
  deliveryPhone: Yup.number().required('Delivery Phone is required'),
  pickupEmail: Yup.string().email('Invalid email').required('Pickup Email is required'),
  deliveryEmail: Yup.string().email('Invalid email').required('Delivery Email is required'),
  startingMileage: Yup.number().required('Starting Mileage is required'),
  endingMileage: Yup.number().required('Ending Mileage is required'),
  totalMilesDriven: Yup.number().required('Total Miles Driven is required'),
  suspensionModel: Yup.string().required('Suspension Model is required'),
  truckCondition: Yup.string().required('Truck Condition is required'),
  year: Yup.number().required('Year is required'),
  airbags: Yup.string().required('Airbags information is required'),
  lockBarsStraps: Yup.string().required('Lock Bars/Straps information is required'),
});

interface TruckLayoutTabProps {
  shippingMode: {
    id?: number;
    mode: string | number;
  };
  onFormChange: (hasChanges: boolean) => void;
  hasUnsavedChanges: boolean;
  saveCurrentForm: () => Promise<void>;
  isOffline: boolean;
  setIsLoading?: (isLoading: boolean) => void;
}

interface FormValues {
  pickupName: string;
  deliveryName: string;
  pickupPhone: string;
  deliveryPhone: string;
  pickupEmail: string;
  deliveryEmail: string;
  startingMileage: string;
  endingMileage: string;
  totalMilesDriven: string;
  suspensionModel?: string;
  truckCondition: string;
  year: string;
  airbags: string;
  lockBarsStraps: string;
  truckLayout: File | null;

}

const TruckLayoutTab: React.FC<TruckLayoutTabProps> = ({ shippingMode, setIsLoading, onFormChange, hasUnsavedChanges, saveCurrentForm, isOffline }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFileDownloading, setIsFileDownloading] = useState(false);
  const { data: layoutData, isFetching: isLoading, refetch } = useGetPostShipTruckTestLayoutDetailsQuery(shippingMode.id, { refetchOnMountOrArgChange: true });
  const [createLayout, { isLoading: isCreating }] = useCreatePostShipTruckTestLayoutMutation();
  const [offlineLayoutData, setOfflineLayoutData] = useState<any>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  // Create separate refs for initial upload and replacement
  const initialUploadRef = useRef<HTMLInputElement>(null);
  const replaceFileRef = useRef<HTMLInputElement>(null);

  const formik = useFormik<FormValues>({
    initialValues: {
      pickupName: '',
      deliveryName: '',
      pickupPhone: '',
      deliveryPhone: '',
      pickupEmail: '',
      deliveryEmail: '',
      startingMileage: '',
      endingMileage: '',
      totalMilesDriven: '',
      // suspensionModel: '',
      truckCondition: '',
      year: '',
      airbags: '',
      lockBarsStraps: '',
      truckLayout: null,
    },
    // validationSchema,
    onSubmit: async (values) => {
      try {
        if (!isOffline) {
          const formData = {
            ...values,
            shippingModeId: shippingMode.id,
            pickupPhone: Number(values.pickupPhone) || 0,
            deliveryPhone: Number(values.deliveryPhone) || 0,
            startingMileage: Number(values.startingMileage) || 0,
            endingMileage: Number(values.endingMileage) || 0,
            totalMilesDriven: Number(values.totalMilesDriven) || 0,
            year: Number(values.year) || 0,
          };

          // If a file is selected, process it
          if (values.truckLayout instanceof File) {
            // We'll handle the file here
            const file = values.truckLayout;

            // Convert the file to a format that can be sent in JSON
            const fileReader = new FileReader();
            fileReader.onload = async (e) => {
              const fileData = e.target?.result;
              if (fileData) {
                // Create a payload with the file data
                const payload = {
                  layoutData: {
                    ...formData,
                    truckLayout: {
                      buffer: Array.from(new Uint8Array(fileData as ArrayBuffer)),
                      originalname: file.name,
                      mimetype: file.type
                    }
                  }
                };

                await createLayout(payload).unwrap();
                refetchAfterSave(refetch);
                setSelectedFileName(null);
                showSuccessToast('Truck layout saved successfully');
                onFormChange(false);
              }
            };

            // Read the file as array buffer
            fileReader.readAsArrayBuffer(file);
          } else {
            // No file to upload, just submit the form data
            const payload = {
              layoutData: formData
            };

            await createLayout(payload).unwrap();

            showSuccessToast('Truck layout saved successfully');
            onFormChange(false);
          }
        } else {
          // Offline mode handling
          const payload = {
            layoutData: {
              ...values,
              shippingModeId: shippingMode.id!,
              pickupPhone: Number(values.pickupPhone) || 0,
              deliveryPhone: Number(values.deliveryPhone) || 0,
              startingMileage: Number(values.startingMileage) || 0,
              endingMileage: Number(values.endingMileage) || 0,
              totalMilesDriven: Number(values.totalMilesDriven) || 0,
              year: Number(values.year) || 0,
            },
          };

          await TruckLayoutService.createTruckLayout(payload);
          showSuccessToast('Truck layout saved successfully');
          onFormChange(false);
        }
      }
      catch (error) {
        showErrorToast('Failed to save truck layout');
        console.error('Error saving truck layout:', error);
      }
    },
  });

  useEffect(() => {
    setIsLoading(formik.isSubmitting);
  }, [formik.isSubmitting, setIsLoading]);

  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await TruckLayoutService.getTruckLayoutDetails(Number(shippingMode.id));
          setOfflineLayoutData(res);
        } catch (error) {
          console.error('Error fetching truck layout data:', error);
        }
      };
      getData();
    }
  }, [isOffline, shippingMode.id]);

  useEffect(() => {
    if (!isOffline) {
      if (layoutData && layoutData.layout) {
        formik.setValues(layoutData.layout as FormValues);

        // If there's a truck layout file, display its information
        if (layoutData.layout.truckLayoutFile) {
          // Just set a friendly name instead of the actual filename
          setSelectedFileName(layoutData.layout.truckLayoutFileName);
        } else {
          setSelectedFileName(null);
        }
      }
    } else {
      if (offlineLayoutData && offlineLayoutData.layout) {
        formik.setValues(offlineLayoutData.layout as FormValues);

        // Handle offline file display
        if (offlineLayoutData.layout.truckLayoutFile) {
          setSelectedFileName("Previously uploaded Excel file");
        } else {
          setSelectedFileName(null);
        }
      }
    }
  }, [layoutData, isOffline, offlineLayoutData]);
console.log(layoutData,'layoutData',formik.values)
  useEffect(() => {
    const hasChanges = JSON.stringify(formik.initialValues) !== JSON.stringify(formik.values);
    onFormChange(hasChanges);
  }, [formik.values, formik.initialValues, onFormChange]);

  // Simplified file handling functions
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type - only accept Excel files
      const validExcelTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        '.xls',
        '.xlsx'
      ];

      const fileType = file.type;
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (!validExcelTypes.includes(fileType) && !['xls', 'xlsx'].includes(fileExtension || '')) {
        showErrorToast('Please upload an Excel file (.xls or .xlsx)');
        return;
      }

      // Store the file in formik state
      formik.setFieldValue("truckLayout", file);

      // Display the file name instead of preview image
      setSelectedFileName(file.name);

      // Show success message
      const isReplacing = selectedFileName === "Previously uploaded Excel file" ||
        (layoutData?.layout?.truckLayoutFile && selectedFileName);
      if (isReplacing) {
        showSuccessToast('File replaced successfully! Don\'t forget to save your changes.');
      } else {
        showSuccessToast('File selected successfully! Don\'t forget to save your changes.');
      }
    }
  };

  // Function to get the full URL for a file download
  const getFullFileUrl = (url: string): string => {
    // If it's a blob URL or already absolute URL (starts with http/https), use as is
    if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // For API media image endpoints, we need special handling
    if (url.startsWith('/api/media/image/')) {
      // This is already a properly formatted proxy URL, we just need to make sure it points to the backend
      // backendApiUrl already includes /api, so we need to remove that from our url
      const blobName = url.split('/api/media/image/')[1];
      const fullUrl = `${backendApiUrl}/media/image/${blobName}`;
      console.log(`🖼️ Image proxy URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // If it's a relative URL (like /api/something), prepend the API URL properly
    if (url.startsWith('/api/')) {
      const fullUrl = `${backendApiUrl}${url.substring(4)}`;
      console.log(`🖼️ API URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // For other relative URLs
    if (url.startsWith('/')) {
      const fullUrl = `${backendApiUrl}${url}`;
      console.log(`🖼️ Relative URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // Default case, just return the URL
    console.log(`🖼️ Using original URL: ${url}`);
    return url;
  };

  // Enhanced download functionality
  const handleExcelDownload = async (url: string) => {
    try {
      console.log('handle downnlload cllicked', url)

      // Use our getFullMediaUrl function to ensure we have the correct URL
      const fullUrl = getFullFileUrl(url);
      console.log(fullUrl, 'downloadUrl')
      console.log(`Downloading from URL: ${fullUrl}`);

      // For proxy URLs, we need to add a download parameter
      const downloadUrl = fullUrl.includes('/api/media/image/')
        ? `${fullUrl}?download=true`
        : fullUrl;

      // Get Okta token for authorization
      const headers: Record<string, string> = {
        'Accept': 'application/json, image/*, video/*, application/octet-stream'
      };

      try {
        const oktaUser = await oktaService.getUser();
        if (oktaUser.access_token) {
          headers['Authorization'] = `Bearer ${oktaUser.access_token}`;
          console.log('Added authorization header for download');
        }
      } catch (tokenError) {
        console.warn('Could not get Okta token for download:', tokenError);
        // Continue without auth header - might still work for public resources
      }

      const response = await fetch(downloadUrl, {
        headers,
        credentials: 'include' // Include cookies if needed
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;

      // Get the filename from the URL or use a default
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0] || 'truck-layout.xlsx';

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      showSuccessToast('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading file:', error);
      showErrorToast('Failed to download file');
    }
  };

  // Add this function for downloading the template specifically
  const handleTemplateDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch('/standard-truck-layout.xlsx', {
        // credentials: 'include' // Include cookies if needed
      });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'standard-truck-layout.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } finally {
      setIsDownloading(false);
    }
  };

  // Add this function for downloading the existing file
  const handleFileDownload = async (url: string) => {
    setIsFileDownloading(true);
    try {
      await handleExcelDownload(url);
    } finally {
      setIsFileDownloading(false);
    }
  };

  if (isCreating || isLoading) return (
    <div className="space-y-6 w-full p-4">
      {/* Header skeleton */}
      <div className="border">
        <div className="w-full flex border-b">
          <div className="p-2 py-3 border-r w-1/3 bg-gray-50">
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="p-2 py-3 border-r w-1/3 bg-gray-50">
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="p-2 py-3 border-r w-1/3 bg-gray-50">
            <Skeleton className="h-6 w-24" />
          </div>
        </div>

        {/* Form fields skeletons - 6 rows with label and 2 inputs */}
        {Array(6).fill(0).map((_, i) => (
          <div key={i} className="flex w-full border-b">
            <div className="p-2 py-3 border-r w-1/3 bg-gray-50">
              <Skeleton className="h-6 w-32" />
            </div>
            <div className="w-1/3 p-2">
              <Skeleton className="h-9 w-full" />
            </div>
            <div className="w-1/3 p-2">
              <Skeleton className="h-9 w-full" />
            </div>
          </div>
        ))}
      </div>

      {/* Truck details section skeleton */}
      <div className="w-full border rounded-md">
        <div className="w-full p-2 bg-gray-50 border-b">
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="p-4 grid grid-cols-2 gap-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Truck layout upload skeleton */}
      <div className="w-full border rounded-md">
        <div className="w-full p-2 bg-gray-50 border-b">
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="p-4">
          <Skeleton className="h-32 w-full" />
          <div className="mt-4 flex gap-4">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
      </div>
    </div>
  );
  // if (error && 'status' in error && error.status !== 404) return <div>Error loading truck layout data</div>;

  return (
    <form id={`postShipTruckLayout-${shippingMode?.id}`} onSubmit={formik.handleSubmit} className="w-full bg-white">
      <div className="border">
        <div className="w-full flex border-b">
          <div className="p-2 py-3 border-r font-semibold label bg-light-blue w-1/3"></div>
          <div className="p-2 py-3 border-r font-semibold label bg-light-blue w-1/3">
            Load Pickup
          </div>
          <div className="p-2 py-3 border-r font-semibold label bg-light-blue w-1/3">
            Load Delivery
          </div>
        </div>

        {/* Name */}
        <div className="flex w-full border-b">
          <div className="p-2 py-3 border-r font-semibold label bg-light-blue w-1/3">
            Name
          </div>
          <div className="relative w-1/3">
            <Input
              {...formik.getFieldProps("pickupName")}
              placeholder="Enter"
              className="border-0 border-l w-full shadow-none focus:ring-0"
            />
            {formik.touched.pickupName && formik.errors.pickupName && (
              <AlertTooltip message={formik.errors.pickupName} />
            )}
          </div>
          <div className="relative w-1/3">
            <Input
              {...formik.getFieldProps("deliveryName")}
              placeholder="Enter"
              className="border-0 border-l w-full shadow-none focus:ring-0"
            />
            {formik.touched.deliveryName && formik.errors.deliveryName && (
              <AlertTooltip message={formik.errors.deliveryName} />
            )}
          </div>
        </div>

        {/* Telephone */}
        <div className="flex w-full border-b">
          <div className="p-2 py-3 border-r font-semibold label bg-light-blue w-1/3">
            Telephone
          </div>
          <div className="relative w-1/3">
            <Input
              {...formik.getFieldProps("pickupPhone")}
              type="number"
              placeholder="Enter"
              className="border-0 border-l w-full shadow-none focus:ring-0"
            />
            {formik.touched.pickupPhone && formik.errors.pickupPhone && (
              <AlertTooltip message={formik.errors.pickupPhone.toString()} />
            )}
          </div>
          <div className="relative w-1/3">
            <Input
              {...formik.getFieldProps("deliveryPhone")}
              placeholder="Enter"
              type="number"
              className="border-0 border-l w-full shadow-none focus:ring-0"
            />
            {formik.touched.deliveryPhone && formik.errors.deliveryPhone && (
              <AlertTooltip message={formik.errors.deliveryPhone.toString()} />
            )}
          </div>
        </div>

        {/* Email */}
        <div className="flex w-full border-b">
          <div className="p-2 py-3 border-r font-semibold label bg-light-blue w-1/3">
            Email
          </div>
          <div className="relative w-1/3">
            <Input
              {...formik.getFieldProps("pickupEmail")}
              placeholder="Enter"
              className="border-0 border-l w-full shadow-none focus:ring-0"
            />
            {formik.touched.pickupEmail && formik.errors.pickupEmail && (
              <AlertTooltip message={formik.errors.pickupEmail} />
            )}
          </div>
          <div className="relative w-1/3">
            <Input
              {...formik.getFieldProps("deliveryEmail")}
              placeholder="Enter"
              className="border-0 border-l w-full shadow-none focus:ring-0"
            />
            {formik.touched.deliveryEmail && formik.errors.deliveryEmail && (
              <AlertTooltip message={formik.errors.deliveryEmail} />
            )}
          </div>
        </div>
      </div>

      {/* Truck Details */}
      <div className="grid grid-cols-3 border mt-6 gap-[2px]">
        {[
          { key: 'startingMileage', label: 'Starting Mileage' },
          { key: 'endingMileage', label: 'Ending Mileage' },
          { key: 'totalMilesDriven', label: 'Total Miles Driven' },
          // { key: 'suspensionModel', label: 'Suspension Model' },
          { key: 'year', label: 'Year', },
          { key: 'truckCondition', label: 'Truck Condition' },
          { key: 'airbags', label: 'Airbags' },
          { key: 'lockBarsStraps', label: 'Lock Bars/Straps' },
        ].map(({ key, label }) => {
          if(key==='truckCondition'){
            return (
              <div className="flex w-full" key={key}>
            <div className="p-2 py-3 border font-semibold label bg-light-blue w-1/2">
              {label}
            </div>
            <div className="relative w-1/2">
              <Select onValueChange={(value) => {
                  if(value!==''){
                    console.log(value,'valuess')
                    formik.setFieldValue(key, value)
                  }
                }} value={formik.values[key]}>
                <SelectTrigger  className=" h-full">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent  >
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>
              {formik.touched[key as keyof FormValues] && formik.errors[key as keyof FormValues] && (
                <AlertTooltip message={formik.errors[key as keyof FormValues]?.toString() || ''} />
              )}
            </div>
          </div>
            )
          }
          else if( key==='lockBarsStraps'){
            return (
              <div className="flex w-full" key={key}>
            <div className="p-2 py-3 border font-semibold label bg-light-blue w-1/2">
              {label}
            </div>
            <div className="relative w-1/2">
              <Select onValueChange={(value) => {
                  if(value!==''){
                    console.log(value,'valuess')
                    formik.setFieldValue(key, value)
                  }
                }} value={formik.values[key]}>
                <SelectTrigger  className=" h-full">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent  >
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="lockBar">Lock Bar</SelectItem>
                  <SelectItem value="straps">Straps</SelectItem>
                </SelectContent>
              </Select>
              {formik.touched[key as keyof FormValues] && formik.errors[key as keyof FormValues] && (
                <AlertTooltip message={formik.errors[key as keyof FormValues]?.toString() || ''} />
              )}
            </div>
          </div>
            )
          }
          else if(key==='airbags'){
            return ( <div className="flex w-full" key={key}>
              <div className="p-2 py-3 border font-semibold label bg-light-blue w-1/2">
                {label}
              </div>
              <div className="relative w-1/2">
                <Select onValueChange={(value) => {
                    if(value!==''){
                      console.log(value,'valuess')
                      formik.setFieldValue(key, value)
                    }
                  }} value={formik.values[key]}>
                  <SelectTrigger  className=" h-full">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent  >
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
                {formik.touched[key as keyof FormValues] && formik.errors[key as keyof FormValues] && (
                  <AlertTooltip message={formik.errors[key as keyof FormValues]?.toString() || ''} />
                )}
              </div>
            </div>)
          }
          else{
            return (
              <div className="flex w-full" key={key}>
            <div className="p-2 py-3 border font-semibold label bg-light-blue w-1/2">
              {label}
            </div>
            <div className="relative w-1/2">
              <Input
                {...formik.getFieldProps(key)}
                placeholder="Enter"
                className="border h-12 w-full shadow-none focus:ring-0"
              />
              {formik.touched[key as keyof FormValues] && formik.errors[key as keyof FormValues] && (
                <AlertTooltip message={formik.errors[key as keyof FormValues]?.toString() || ''} />
              )}
            </div>
          </div>
            )
          }
        })}
      </div>

      {/* File Upload */}
      <div className="my-4">
        {(formik.values.truckLayout || layoutData?.layout?.truckLayoutFile) && selectedFileName ? (
          <div className="w-full px-4 py-4 flex items-center justify-between bg-white border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className={cn(
                "p-2 rounded-md mr-3",
                isFileDownloading ? "bg-blue-50" : "bg-green-50"
              )}>
                {isFileDownloading ? (
                  <Loader2 size={20} className="text-blue-600 animate-spin" />
                ) : (
                  <FileIcon className="text-green-600" />
                )}
              </div>
              <div className="flex flex-col">
                <span className="font-medium text-gray-800">{selectedFileName}</span>
                <span className="text-sm text-gray-500">
                  {isFileDownloading
                    ? "Downloading Excel file..."
                    : "Excel Document • Ready to use"}
                </span>
              </div>
            </div>
            <DropdownMenu >
              <DropdownMenuTrigger
                className={cn(
                  "rounded-full !pointer-events-auto  text-gray-600 p-2 font-semibold text-sm flex capitalize items-center gap-2 outline-none hover:bg-gray-100"
                )}
                disabled={isFileDownloading}
              >
                <span>
                  <Ellipsis size={18} />
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="rounded-lg p-0 shadow-md border border-gray-200">
                <DropdownMenuItem
                  className="p-2 hover:bg-gray-50"
                  onClick={(e) => {
                    e.preventDefault();

                    // Trigger the replacement file input
                    if (replaceFileRef.current) {
                      replaceFileRef.current.click();
                    }
                  }}
                  title="Replace with a different Excel file"
                  disabled={isFileDownloading}
                >
                  <span className="text-blue-600">
                    <RefreshCcw size={16} />
                  </span>
                  &nbsp; Replace
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-gray-200" />
                {layoutData?.layout?.truckLayoutFile && (
                  <>
                    <DropdownMenuItem
                      className="p-2 hover:bg-gray-50"
                      onClick={() => {
                        if (layoutData.layout.truckLayoutFile) {
                          handleFileDownload(layoutData.layout.truckLayoutFile);
                        }
                      }}
                      disabled={isFileDownloading}
                    >
                      <span className="text-blue-600">
                        <Download size={16} />
                      </span>
                      &nbsp; Download
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-200" />
                  </>
                )}
                {/* <DropdownMenuItem
                  onClick={() => {
                    setSelectedFileName(null);
                    formik.setFieldValue("truckLayout", null);
                  }}
                  className="p-2 hover:bg-gray-50 text-red-500"
                  disabled={isFileDownloading}
                >
                  <span>
                    <Trash size={16} />
                  </span>{" "}
                  &nbsp; Delete
                </DropdownMenuItem> */}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <>
            <Button
              className="bg-white flex-center justify-start text-gray-600 relative w-full border-dashed border-2 hover:bg-gray-50 transition-colors rounded-md shadow-sm py-4 px-4"
              onClick={(e) => {
                e.preventDefault();

                // Trigger the initial file input
                if (initialUploadRef.current) {
                  initialUploadRef.current.click();
                }
              }}
              type="button"
            >
              <img src={"/upload.svg"} alt="Upload" />
              &nbsp; &nbsp;
              <span>Upload Truck Layout (Excel file)</span>
            </Button>

            {/* Initial upload file input */}
            <input
              type="file"
              ref={initialUploadRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              aria-label="Upload truck layout Excel file"
            />
          </>
        )}

        {/* Separate file input for replacement (always hidden) */}
        <input
          type="file"
          ref={replaceFileRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          aria-label="Replace truck layout Excel file"
        />
      </div>

      <Button
        className="bg-white flex-center !pointer-events-auto  justify-start text-gray-600 relative w-full border-dashed border-2 hover:bg-gray-50 transition-colors rounded-md shadow-sm py-4 px-4 mt-2"
        type="button"
        onClick={handleTemplateDownload}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <div className="flex items-center">
            <Loader2 size={20} className="animate-spin mr-2" />
            <span className="font-medium">Downloading template...</span>
          </div>
        ) : (
          <>
            <img src={"/upload.svg"} className="rotate-180" alt="Upload" />
            &nbsp; &nbsp;
            <span>Click here to download the standard template</span>
          </>
        )}
      </Button>
      <button type="submit" style={{ display: "none" }} title="Submit form"></button>
    </form>
  );
};

export default TruckLayoutTab;
